import { useCallback } from 'react'
import { Button } from '@highlight/ui'
import { useCreditsTopUp, useEnrichedBilling } from '@renderer/chat'
import { useSetSidepanelState } from '@renderer/overlay/atoms'
import { Toast, useToast } from '@renderer/overlay/Toaster'

interface TranscriptionDowngradedToastProps {
  toastId: string
}

/**
 * Shown when a cloud-session outage (non-403) downgrades a recording to local
 * transcription. Billing gates refuse the start with a paywall instead
 * (MEETING_LIMIT_REACHED_EVENT), so this should be rare. Copy and CTA routing
 * mirror the NagBar local-transcription variant (NagBar.tsx).
 */
export const TranscriptionDowngradedToast = ({ toastId }: TranscriptionDowngradedToastProps) => {
  const toast = useToast()
  const { isTrial, isPro } = useEnrichedBilling()
  const { tier: creditsTier, openCreditsPurchase } = useCreditsTopUp()
  const setSidepanelState = useSetSidepanelState()

  // Only a paid plan a top-up can bill buys its way back to cloud
  // transcription from here. Free, trial and legacy users are just told.
  const isPaidPro = isPro && !isTrial && creditsTier !== null

  const message = isPaidPro
    ? 'You are using lower quality local transcription, add credits for the best meeting notes.'
    : 'You are using lower quality local transcription for these meeting notes.'

  const handleAddCredits = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      toast.dismiss(toastId)
      // The paywall renders inside the sidepanel, so make sure it's visible.
      setSidepanelState('open')
      openCreditsPurchase('transcription downgraded toast')
    },
    [toast, toastId, setSidepanelState, openCreditsPurchase],
  )

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Description>{message}</Toast.Description>
        {isPaidPro && (
          <Toast.Actions>
            <Button size="sm" variant="primary" onClick={handleAddCredits}>
              Add Credits
            </Button>
          </Toast.Actions>
        )}
      </Toast.Content>
    </Toast>
  )
}
